import Axios from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { StepWizardChildProps } from 'react-step-wizard';
import { ContentTypes, loadComponent } from '../../blocks';
import { HexpertContext, HexpertState } from '../../contexts/HexPertProvider';
import { PageQuestion } from '../../types/hexpertTypes';
import { getUnitForField } from '../../helpers/unitsConversion';
import { Container } from './Container';
import { Hexpert as endpoints } from '@services/endpoints';
import dataLayer from '@helpers/dataLayer';

enum FormFields {
  FORM_GUID = '__FormGuid',
  FORM_LANGUAGE = '__FormLanguage',
  FORM_HOSTED_PAGE = '__FormHostedPage',
  FORM_ANTI_FORGERY = '__RequestVerificationToken',
}

type TFormSection = {
  title?: string;
  label?: string;
  description?: string;
  setStartSteps?: (startPosition: HexpertState['startSteps']) => void;
  formHeader?: JSX.Element;
  children?: JSX.Element[] | JSX.Element;
  questions?: PageQuestion[];
  showNextButton: boolean;
  determineFurtherQuestions?: boolean;
  shouldSubmit?: boolean;
} & Partial<StepWizardChildProps>;

export const FormSection = ({
  title,
  description,
  setStartSteps,
  formHeader,
  questions,
  nextStep,
  previousStep,
  currentStep,
  showNextButton,
  determineFurtherQuestions,
  shouldSubmit,
}: TFormSection) => {
  const { state, dispatch } = useContext(HexpertContext);
  const [requiredQuestions, setRequiredQuestions] = useState([]);

  useEffect(() => {
    if (state?.startSteps === 'start') {
      dataLayer.push('page_view', {
        page_category: state?.hexpertSteps?.slice(0, currentStep + 2),
        page_path: state?.hexpertSteps?.slice(0, currentStep + 2).join('/'),
        page_title: `Hexpert-${state?.currentForm?.title} - ${title}`,
      });
    }
  }, []);

  const questionValidator = () => {
    const required = [];
    questions.forEach((q) => {
      // add questions with options or slider values to required.
      if (q.Options?.length || q.Max || q.PredefinedValue) {
        required.push(q.FieldId);
        const hasCondition = q.conditionQuestions?.length > 0 ? q.FieldId : null;
        // check if there are any nest conditional questions
        if (hasCondition && state?.formAnswers) {
          q?.conditionQuestions?.forEach((conditionQuestion) => {
            const isPartOfSelected = conditionQuestion?.Conditions?.some((condition) => {
              return condition.FieldValue === state?.formAnswers[hasCondition];
            });
            if (isPartOfSelected) {
              required.push(conditionQuestion.FieldId);
            }
          });
        }
      } else {
        return;
      }
    });
    setRequiredQuestions(required);
  };

  useEffect(() => {
    questionValidator();
  }, [state?.formAnswers]);

  const checkForm = () => {
    dispatch({ type: 'formCheckLoading', payload: true });
    dispatch({ type: 'setForceSteps', payload: null });
    dispatch({ type: 'showLastQuestions', payload: false });
    dispatch({ type: 'submitOnNext', payload: false });

    const indexOfMiddleSubmit = state.currentForm.sections?.findIndex((s) => s.isSubmit);
    const clearAdditionalQuestionAnswers = state.currentForm?.sections?.slice(
      indexOfMiddleSubmit + 1,
      state.currentForm.sections?.length
    );
    // clear all answers for additional questions
    clearAdditionalQuestionAnswers.forEach((a) =>
      a.questions.forEach((q) => dispatch({ type: 'removeAnswer', payload: q.FieldId }))
    );

    const form = new FormData();
    form.append(FormFields.FORM_GUID, state.currentForm.formGuid);
    form.append(FormFields.FORM_LANGUAGE, window['lang']);
    form.append(FormFields.FORM_HOSTED_PAGE, state.pageProps?.PageId.toString());
    state.formAnswers &&
      Object.entries(state.formAnswers).forEach((val) => form.append(val[0], val[1]));
    Axios.post(endpoints.getIsMatchingProducts, form)
      .then((res) => {
        dispatch({ type: 'formCheckLoading', payload: false });

        dispatch({ type: 'submitOnNext', payload: res?.data === false });
        if (res?.data === false) {
        } else {
          dispatch({ type: 'showLastQuestions', payload: true });
        }
      })
      .catch((e) => {
        // fail assume we should submit on next
        dispatch({ type: 'submitOnNext', payload: true });
        dispatch({ type: 'formCheckLoading', payload: false });
      });
  };

  const submitForm = (state: HexpertState, showResultAfter?: boolean) => {
    dispatch({ type: 'loadingResult', payload: true });
    Axios.get(endpoints.getAntiForgeryToken).then((antiforgery) => {
      console.log('antiforgery ', antiforgery);
      const form = new FormData();
      form.append(FormFields.FORM_GUID, state.currentForm.formGuid);
      form.append(FormFields.FORM_ANTI_FORGERY, antiforgery.data);
      form.append(FormFields.FORM_LANGUAGE, window['lang']);
      form.append(FormFields.FORM_HOSTED_PAGE, state.pageProps?.PageId.toString());

      state.formAnswers &&
        Object.entries(state.formAnswers).forEach((val) => form.append(val[0], val[1]));
      Axios.post(endpoints.submitHexpertForm, form, {
        headers: {
          ['antiforgerytoken']: antiforgery.data,
        },
      }).then((submitRes) => {
        if (submitRes.data.isSuccess) {
          return;
        }
        let redirectUrl = submitRes.data.RedirectUrl?.split('?')[1] ?? '';
        const tempUrl = redirectUrl.split('//');
        if (tempUrl.length > 1) redirectUrl = '/' + tempUrl[1].split('/').slice(1).join('/');
        if (!redirectUrl || redirectUrl?.length < 1) {
          dispatch({ type: 'loadingResult', payload: false });
        }

        Axios.get(endpoints.getHexpertResult + '?' + redirectUrl, {
          params: {
            culture: window['lang'],
          },
        })
          .then((res) => {
            dispatch({ type: 'loadingResult', payload: false });

            dispatch({
              type: 'setResult',
              payload: {
                redirectUrl: window && window?.location?.origin + submitRes.data?.RedirectUrl,
                ...res.data,
                Summary: {
                  ...res.data.Summary,
                  FormDataItems: res.data.Summary.FormDataItems.map((item) => ({
                    ...item,
                    unit: getUnitForField(item.FieldName, state.pageProps.Forms),
                  })),
                },
              },
            });
          })
      });
    });
  };

  return (
    <Container
      title={title}
      description={description}
      setStartSteps={setStartSteps}
      formHeader={currentStep === 1 && formHeader} // need to add in backend should maybe be in the separator block?
      nextStep={nextStep}
      currentStep={currentStep}
      previousStep={previousStep}
      loading={state?.formCheckLoading}
      showNextButton={
        requiredQuestions?.length &&
        requiredQuestions.every(
          (req) => state?.formAnswers && Object.keys(state?.formAnswers).includes(req)
        )
      }
      submitLabel={
        questions.find((question) => question.ContentType === 'WeFitSubmitButtonElementBlock')
          ?.Label
      }
      submit={
        state?.submitOnNext && shouldSubmit
          ? () => {
            submitForm(state);
            dispatch({ type: 'showProductResult', payload: true });
          }
          : determineFurtherQuestions
            ? () => {
              checkForm();
              nextStep();
            }
            : questions.find((question) => question.ContentType === 'WeFitSubmitButtonElementBlock')
              ? () => {
                dispatch({ type: 'showProductResult', payload: true });
                submitForm(state, true);
              }
              : null
      }
    >
      <>
        {questions?.map((question) => {
          return loadComponent(question.ContentType as ContentTypes, question, nextStep);
        })}
      </>
    </Container>
  );
};
